// Default rate configuration seeded from the source-of-truth workbook:
//   "2025-TZG Combined BR + Permanent Template - Updated.xlsx"
//
// All values were verified by reading the workbook with openpyxl.
// Where the Rate Tables sheet disagrees with the Builders Risk Indicator
// sheet, the Builders Risk Indicator sheet wins (those are the values the
// live formulas consume).
//
// Cell references are included as inline comments so future maintainers can
// reproduce the lookup.
export const DEFAULT_RATE_CONFIG = {
    // Rate Tables!A5:B8
    constructionMultipliers: {
        Frame: 1.0,
        JoistedMasonry: 0.9,
        NonCombustible: 0.8,
        FireResistive: 0.7,
    },
    // Rate Tables!F5:G12 — only the 5 priced states. OTHER is intentionally
    // absent: selecting OTHER bypasses calculation and routes to a contact form.
    stateTaxRates: {
        MI: 0.04,
        GA: 0.04,
        FL: 0.05,
        TX: 0.048,
        CA: 0.03,
    },
    baseRates: {
        // Rate Tables!B12:D12 (also mirrored at Builders Risk Indicator!I13/J13/K13).
        builderRisk: {
            best: 0.0045,
            average: 0.005,
            conservative: 0.0055,
        },
        // Rate Tables!B13:D13 (also at Builders Risk Indicator!I24/J24/K24).
        oiGl: {
            best: 0.65,
            average: 0.71,
            conservative: 0.8,
        },
        // Builders Risk Indicator!I34/J34/K34 — these are the values the live
        // Excess formula consumes. Rate Tables!B14:D14 contains 0.004/0.0045/0.005
        // but the BR Indicator sheet uses 0.4/0.45/0.5 (per $1,000). We trust the
        // BR Indicator sheet because that is what the workbook's formulas read.
        excess: {
            best: 0.4,
            average: 0.45,
            conservative: 0.5,
        },
        // Rate Tables!B15:D15 (also at Builders Risk Indicator!I45/J45/K45).
        pollution: {
            best: 0.38,
            average: 0.42,
            conservative: 0.48,
        },
        // Permanent Insurance!C23/E23 — per $100 of building value (hard costs).
        // Note: Rate Tables!A16 lists Average 0.40 / Conservative 0.45, but the
        // Permanent sheet itself uses only two modes (aggressive / conservative)
        // with values 0.35 / 0.45. Trust the Permanent sheet.
        permanentProperty: {
            aggressive: 0.35,
            conservative: 0.45,
        },
        // Permanent Insurance!C37/E37 — per unit. Rate Tables!A17 lists
        // 85 / 95 / 110; the Permanent sheet uses 85 / 110 for its two modes.
        permanentGl: {
            aggressive: 85,
            conservative: 110,
        },
    },
    // Rate Tables!A21:D26 — dropdown options in the workbook. Property AOP /
    // Property Flood exist in the Rate Tables sheet but the Permanent
    // Insurance deductibles are currently fixed per scenario (not prospect-
    // selectable) and are therefore not exposed here.
    deductibleOptions: {
        brAop: [25000, 50000, 100000],
        brFlood: [100000, 250000, 500000],
        brWaterDamage: [50000, 100000, 250000],
        oiGl: [25000, 50000, 100000],
        pollution: [5000, 10000, 25000],
    },
    // Deductible credit factor tables.
    // BR AOP     — Rate Tables!B33:C35
    // BR Flood   — Rate Tables!F33:G35
    // BR Water   — Rate Tables!J33:K35
    // OI GL      — Rate Tables!B38:C40
    // Pollution  — Rate Tables!F38:G40
    deductibleCreditFactors: {
        brAop: [
            { deductible: 25000, credit: 1.0 },
            { deductible: 50000, credit: 0.95 },
            { deductible: 100000, credit: 0.9 },
        ],
        brFlood: [
            { deductible: 100000, credit: 1.0 },
            { deductible: 250000, credit: 0.95 },
            { deductible: 500000, credit: 0.9 },
        ],
        brWaterDamage: [
            { deductible: 50000, credit: 1.0 },
            { deductible: 100000, credit: 0.95 },
            { deductible: 250000, credit: 0.9 },
        ],
        oiGl: [
            { deductible: 25000, credit: 1.0 },
            { deductible: 50000, credit: 0.95 },
            { deductible: 100000, credit: 0.9 },
        ],
        pollution: [
            { deductible: 5000, credit: 1.0 },
            { deductible: 10000, credit: 0.95 },
            { deductible: 25000, credit: 0.9 },
        ],
    },
    // Rate Tables!G11.
    triaRate: 0.04,
    scenarioLabels: {
        best: 'Best Case',
        average: 'Average',
        conservative: 'Conservative',
    },
    configVersion: '2025-tzg-combined-v1',
};
/**
 * Look up the state tax rate. Returns null for OTHER (the "request custom
 * quote" bucket) so callers know to route to the contact form rather than
 * attempt calculation with a missing rate.
 */
export function lookupStateTaxRate(state, config = DEFAULT_RATE_CONFIG) {
    if (state === 'OTHER')
        return null;
    const rate = config.stateTaxRates[state];
    return rate ?? null;
}
//# sourceMappingURL=rates.js.map